import { Space } from "antd";
import AttributeSection from "features/cases/blocks/AttributeSection";
import CaseSolutions from "features/cases/blocks/CaseSolutions";
import { arePrimaryAttrsFilled } from "features/cases/utils";
import SortToggle from "feeds/SortToggle";
import { useDataQuery } from "hooks/useData";
import { useSortState } from "hooks/useSortState";
import { useUser } from "hooks/useUser";
import React, { useMemo } from "react";
import { makeSortByMetaDate } from "utils/basic";

import AddContentCard from "./elements/AddContentCard";
import CaseContentMainCard from "./elements/CaseContentMainCard";
import ProblemDrawer from "./elements/ProblemDrawer";
import { useHashScroll } from "./hooks/useHashScroll";

import "./CaseContent.css";

// Старый блок решений вытеснен вкладками в CaseContentMainCard; оставлен под флагом для отката.
const SHOW_LEGACY_SOLUTIONS_LIST = false;

const CaseContent = ({
  problemId = "",
  solutionId = "",
  kind = "problem",
  setProbExpanded,
  probExpanded = false,
  setCaseExpanded,
  caseExpanded = false,
  isAdding = false,
}: {
  problemId?: string;
  solutionId?: string;
  kind?: "problem" | "solution";
  setProbExpanded?: (expanded: boolean) => void;
  probExpanded?: boolean;
  setCaseExpanded?: (expanded: boolean) => void;
  caseExpanded?: boolean;
  isAdding?: boolean;
}) => {
  const { isContributor } = useUser();

  // Клиентская сортировка карточек во всех блоках атрибутов сразу
  const { sortField, sortDirection, dateDisplayMode, onSortChange } =
    useSortState();

  const problemAttrs: any[] = useDataQuery("attrs/?attributeOf=problem");
  const solutionAttrs: any[] = useDataQuery("attrs/?attributeOf=solution");

  const attrs = kind === "problem" ? problemAttrs : solutionAttrs;

  const cardsProblem = useDataQuery(
    `problems/${problemId}/cards/`,
    !problemId,
    [],
  );
  const problem = useDataQuery(`problems/${problemId}`, !problemId, null);
  const solutionList = useDataQuery(
    `problems/${problemId}/solutions/`,
    !problemId,
    [],
  );

  const solution = useMemo(() => {
    if (!solutionList) return null;

    return solutionList.find((sol: any) => sol.id == solutionId);
  }, [solutionList, solutionId]);

  const variants = useDataQuery(`attribute_variants/`);

  const cardsSolution: any[] = useDataQuery(
    `problems/${problemId}/solutions/${solutionId}/cards/`,
    !solutionId || !problemId,
    [],
  );

  const attrCards: any[] = kind === "problem" ? cardsProblem : cardsSolution;
  const primaryAttrsFilled = arePrimaryAttrsFilled(attrs, attrCards);

  const contextHolder = useHashScroll(attrCards);

  if (!attrCards) return <div>Loading attribute cards...</div>;
  if (!attrs) return <div>Loading attributes...</div>;

  if (problemId && isAdding && isContributor)
    return (
      <AddContentCard
        kind="solution"
        problemId={problemId}
        sphere={problem?.sphere}
      />
    );

  if (!problem && isAdding && isContributor)
    return (
      <AddContentCard
        kind="problem"
        sphere={problem?.sphere}
      />
    );

  if (!problem) return <div>Loading problem...</div>;

  return (
    <Space
      id="ContainerElementID"
      direction="vertical"
      size="middle"
      style={{ display: "flex" }}
    >
      {contextHolder}

      <CaseContentMainCard
        kind={kind}
        problem={problem}
        solution={solution}
        solutionList={solutionList}
        probExpanded={probExpanded}
        setProbExpanded={setProbExpanded}
      />

      {attrCards.length > 1 && (
        <div className="case-content__sort">
          <SortToggle
            onSortChange={onSortChange}
            initialField={sortField}
            initialDirection={sortDirection}
            size="small"
          />
        </div>
      )}

      {attrs.map((attr) => {
        const cards = attrCards
          .filter(
            (card) => card.attr === attr.id || card.attributeType === attr.id,
          )
          .sort(makeSortByMetaDate(sortField, sortDirection));
        const attributeOf = kind === "problem" ? "problem" : "solution";
        const disabled =
          isContributor && !primaryAttrsFilled && !attr.isPrimary;

        if (!cards.length && !isContributor) return null;

        return (
          <AttributeSection
            key={attr.id}
            attr={attr}
            disabled={disabled}
            cards={cards}
            variants={variants}
            attributeOf={attributeOf}
            problemId={problemId}
            solutionId={solutionId}
            dateDisplayMode={dateDisplayMode}
          />
        );
      })}

      {SHOW_LEGACY_SOLUTIONS_LIST && (
        <CaseSolutions
          problemId={problemId}
          solutionId={solutionId}
          solutionList={solutionList}
        />
      )}

      {kind === "solution" && (
        <ProblemDrawer
          problemId={problemId}
          problem={problem}
          isOpen={caseExpanded}
          onClose={() => setCaseExpanded?.(false)}
        />
      )}
    </Space>
  );
};

export default CaseContent;
